import { c as _c } from "react/compiler-runtime";
import React from 'react';
import Modal, { StandardModal } from '.';
import { Box, Flex, SxStyleProp } from '../grid';
import Portal from '../portal';
import { ModalWindowProps } from './component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpRightFromSquare } from '@fortawesome/pro-solid-svg-icons';
import { storageFactory } from '../storage-factory';

// import { useStateWithRef } from '../hooks';
// import { iOS } from '../utils/detect';

interface ModalContextInterface {
  size: ModalWindowProps['size'];
  sizeOptions?: ModalWindowProps['size'][];
  changeSize?: (newSize: string) => void;
}
export const ModalContext = React.createContext<ModalContextInterface>({
  size: 'medium',
  sizeOptions: [],
  changeSize: (newSize: string) => {}
});
export const useModalContext = () => {
  return React.useContext(ModalContext);
};
export interface OpenModalFn {
  (title: string, getContent: (args: {
    close: () => void;
  } & Record<string, any>) => React.JSX.Element, options?: {
    size?: ModalWindowProps['size'];
    onClose?: () => void;
    leftCorner?: React.ReactNode;
    rightCorner?: React.ReactNode;
    type?: 'standard' | 'blank';
    closeWithHookCleanup?: boolean;
    zIndexDelta?: number;
    sx?: SxStyleProp;
    id?: string;
    memoKey?: string;
    sizeOptions?: string[];
  }): () => void;
}
interface ModalToastContextInterface {
  openModal: OpenModalFn;
  modalCount: number;
  setModalCount: React.Dispatch<React.SetStateAction<number>>;
  hasLeftPanel?: boolean;
  hasRightPanel?: boolean;
}
export const ModalToastContext = React.createContext<ModalToastContextInterface>({
  openModal: () => null,
  modalCount: 0,
  setModalCount: (fn: React.SetStateAction<number>) => {},
  hasLeftPanel: false,
  hasRightPanel: false
});

// const isIOS = true; //iOS();

const local = storageFactory(() => localStorage);
export const ModalToastProvider = t0 => {
  const $ = _c(25);
  const {
    children
  } = t0;
  const [modalCount, setModalCount] = React.useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [modalStack, setModalStack] = React.useState(t1);
  let t2;
  if ($[1] !== modalStack) {
    t2 = (title, getContent, t3) => {
      const {
        size: t4,
        onClose,
        leftCorner,
        rightCorner,
        type: t5,
        zIndexDelta: t6,
        sx: t7,
        id,
        memoKey,
        sizeOptions: sizeOptions_
      } = t3;
      const requestedSize = t4 === undefined ? "medium" : t4;
      const type = t5 === undefined ? "standard" : t5;
      const zIndexDelta = t6 === undefined ? 0 : t6;
      const sx = t7 === undefined ? {} : t7;
      const modalId = id || Math.random().toString(36).substring(7);
      const sizeMemoKey = `modal-${memoKey || id || title}-size`;
      const size = local.getItem(sizeMemoKey) || requestedSize;
      const close = () => {
        onClose?.();
        setModalStack(v => v.filter(m => m.id !== modalId));
      };
      const isDirtyRef = React.createRef();
      const backdropRef = React.createRef();
      const hasUnsavedChanges = () => {
        if (typeof isDirtyRef.current === "function") {
          return isDirtyRef.current();
        }
        return isDirtyRef.current;
      };
      const changeSize = newSize => {
        if (newSize !== "minimized") {
          local.setItem(sizeMemoKey, newSize);
        }
        setModalStack(v_0 => v_0.map(m_0 => {
          if (m_0.id === modalId) {
            return {
              ...m_0,
              size: newSize
            };
          }
          return m_0;
        }));
      };
      const sizeOptions = sizeOptions_ ? [...sizeOptions_] : [];
      if (!sizeOptions.includes(requestedSize)) {
        sizeOptions.unshift(requestedSize);
      }
      const existingModal = modalStack.find(m_1 => m_1.id === modalId);
      const modalItem = existingModal || {
        id: modalId,
        title,
        content: getContent({
          close,
          isDirtyRef,
          backdropRef
        }),
        size,
        originalSize: size,
        changeSize,
        sizeOptions,
        close,
        leftCorner,
        rightCorner,
        hasUnsavedChanges,
        backdropRef,
        type,
        zIndexDelta,
        sx,
        memoKey
      };
      if (!existingModal) {
        setModalStack(v_1 => [...v_1, modalItem]);
      } else {
        setModalStack(v_2 => {
          const existingModal_0 = v_2.find(m_2 => m_2.id === modalId);
          const newStack = v_2.filter(m_3 => m_3.id !== modalId).concat({
            ...existingModal_0,
            size
          });
          return newStack;
        });
      }
      return close;
    };
    $[1] = modalStack;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const openGlobalModal = t2;
  let t3;
  if ($[3] !== modalStack) {
    t3 = modalStack.filter(_temp).map(_temp2);
    $[3] = modalStack;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const minimizedModals = t3;
  let t4;
  if ($[5] !== modalStack) {
    t4 = modalStack.map(_temp4);
    $[5] = modalStack;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const modals = t4;
  let t5;
  if ($[7] !== modalStack) {
    t5 = modalStack.some(_temp5);
    $[7] = modalStack;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const hasLeftPanel = t5;
  let t6;
  if ($[9] !== modalStack) {
    t6 = modalStack.some(_temp6);
    $[9] = modalStack;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const hasRightPanel = t6;
  let t7;
  if ($[11] !== hasLeftPanel || $[12] !== hasRightPanel || $[13] !== modalCount || $[14] !== openGlobalModal) {
    t7 = {
      openModal: openGlobalModal,
      modalCount,
      setModalCount,
      hasLeftPanel,
      hasRightPanel
    };
    $[11] = hasLeftPanel;
    $[12] = hasRightPanel;
    $[13] = modalCount;
    $[14] = openGlobalModal;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const value = t7;
  let t8;
  if ($[16] !== modals) {
    t8 = <Portal>{modals}</Portal>;
    $[16] = modals;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== minimizedModals) {
    t9 = minimizedModals.length != 0 && <Portal><Flex sx={{
        position: "fixed",
        bottom: 0,
        right: 8,
        gap: 2,
        zIndex: 6005
      }}>{minimizedModals}</Flex></Portal>;
    $[18] = minimizedModals;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== children || $[21] !== t8 || $[22] !== t9 || $[23] !== value) {
    t10 = <ModalToastContext.Provider value={value}>{children}{t8}{t9}</ModalToastContext.Provider>;
    $[20] = children;
    $[21] = t8;
    $[22] = t9;
    $[23] = value;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  return t10;
};
function _temp(m_4) {
  return m_4.size === "minimized";
}
function _temp2(t0, idx) {
  const {
    id: id_0,
    title: title_0,
    changeSize: changeSize_0,
    originalSize,
    memoKey: memoKey_0
  } = t0;
  return <Box key={id_0} id={`minimized-modal-${id_0}`} bg="text" as="button" type="button" onClick={() => {
    console.log({
      memoKey: memoKey_0
    });
    const sizeMemoKey_0 = `modal-${memoKey_0 || id_0 || title_0}-size`;
    const size_0 = local.getItem(sizeMemoKey_0) || originalSize;
    changeSize_0(size_0);
  }} px={2} py={2} color="invertedText" fontWeight="heading" borderTopLeftRadius={2} borderTopRightRadius={2} data-sentry-element="Box" data-sentry-component="_temp2" data-sentry-source-file="context.tsx">{title_0}<FontAwesomeIcon icon={faUpRightFromSquare} style={{
      marginLeft: 8,
      opacity: 0.8
    }} size="sm" data-sentry-element="FontAwesomeIcon" data-sentry-source-file="context.tsx" /></Box>;
}
function _temp3(t0) {
  const {
    content: content_0
  } = t0;
  return content_0;
}
function _temp4(t0) {
  const {
    id: id_1,
    title: title_1,
    content,
    size: size_1,
    close: close_0,
    leftCorner: leftCorner_0,
    rightCorner: rightCorner_0,
    hasUnsavedChanges: hasUnsavedChanges_0,
    backdropRef: backdropRef_0,
    type: type_0,
    zIndexDelta: zIndexDelta_0,
    changeSize: changeSize_1,
    sizeOptions: sizeOptions_0,
    sx: sx_0
  } = t0;
  const ModalComponent = type_0 === "blank" ? Modal : StandardModal;
  return <ModalComponent usePortal={false} key={id_1} isOpen={true} close={close_0} title={type_0 !== "blank" ? title_1 : undefined} props={{
    content
  }} size={size_1} leftCorner={leftCorner_0} rightCorner={rightCorner_0} hasUnsavedChanges={hasUnsavedChanges_0} backdropRef={backdropRef_0} zIndexDelta={zIndexDelta_0} changeSize={changeSize_1} sizeOptions={sizeOptions_0} sx={sx_0} data-sentry-element="ModalComponent" data-sentry-component="_temp4" data-sentry-source-file="context.tsx">{_temp3}</ModalComponent>;
}
function _temp5(m_5) {
  return m_5.size === "left";
}
function _temp6(m_6) {
  return m_6.size === "right";
}